import { lazy, Suspense } from 'react';
import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { DEFAULT_WORLD_SURFACE, isWorldSurface } from '@/lib/worlds';
import { ChunkLoadErrorBoundary } from '../components/ChunkLoadErrorBoundary';

// Lazy load route components for code splitting
const BridgePage = lazy(() => import('./pages/BridgePage'));
const IpDashboardIndexPage = lazy(() => import('./pages/IpDashboardIndexPage'));
const CharacterWorkspacePage = lazy(() => import('./pages/CharacterWorkspacePage'));
const EnvironmentWorkspacePage = lazy(() => import('./pages/EnvironmentWorkspacePage'));
const AssetLibraryPage = lazy(() => import('./pages/AssetLibraryPage'));
const WorldSurfacePage = lazy(() => import('./pages/WorldSurfacePage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const PeoplePage = lazy(() => import('./pages/admin/PeoplePage'));
const EditUserPage = lazy(() => import('./pages/admin/EditUserPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const GrantsPage = lazy(() => import('./pages/admin/GrantsPage'));
const CapabilitiesPage = lazy(() => import('./pages/admin/CapabilitiesPage'));
const QuorumsPage = lazy(() => import('./pages/admin/QuorumsPage'));
const ApplicationScopesPage = lazy(() => import('./pages/admin/ApplicationScopesPage'));
const KeysPage = lazy(() => import('./pages/admin/KeysPage'));
const ProvidersPage = lazy(() => import('./pages/admin/ProvidersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NexusPage = lazy(() => import('./pages/NexusPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const LoomPage = lazy(() => import('./pages/LoomPage'));
const ReadingListsPage = lazy(() => import('./pages/ReadingListsPage'));
const ReadingListPage = lazy(() => import('./pages/ReadingListPage'));
const SpecEditorPage = lazy(() => import('./pages/SpecEditorPage'));
const WorkPage = lazy(() => import('./pages/work/WorkPage'));
const WorkTeamsPage = lazy(() => import('./pages/work/WorkTeamsPage'));
const WorkIssuesPage = lazy(() => import('./pages/work/WorkIssuesPage'));
const WorkIssuePage = lazy(() => import('./pages/work/WorkIssuePage'));
const WorkSprintsPage = lazy(() => import('./pages/work/WorkSprintsPage'));
const WorkLabelsPage = lazy(() => import('./pages/work/WorkLabelsPage'));
const WorkProjectsPage = lazy(() => import('./pages/work/WorkProjectsPage'));
const WorkProjectsOverviewPage = lazy(() => import('./pages/work/WorkProjectsOverviewPage'));
const WorkProjectPage = lazy(() => import('./pages/work/WorkProjectPage'));
const MeshPage = lazy(() => import('./pages/MeshPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Loading fallback component
function RouteLoading() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  );
}

// Wrapper for lazy components — catches stale chunk errors after deploys
function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return function SuspenseWrapper() {
    return (
      <ChunkLoadErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
          <Component />
        </Suspense>
      </ChunkLoadErrorBoundary>
    );
  };
}

/**
 * Root route - provides the router outlet
 */
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

/**
 * Index route - the Bridge, for every lens template (CDEV-1995).
 *
 * Templates differ in scope order (and later the Bridge's default standing
 * stance), not in where they land: the index no longer redirects to
 * resolvedLens.landingRoute, which every bootstrap template now sets to "/".
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: withSuspense(BridgePage),
});

/**
 * Worlds (CDEV-2028). A world is one property; the rail lists the surfaces of
 * the world in focus and the URL carries it: /worlds/$worldId/$surface, with
 * drill-downs under their surface. The index is every world, and the place to
 * create and fork. (Moved from "/" when the Bridge took the index, CDEV-1995.)
 */
const worldsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/worlds',
  component: withSuspense(IpDashboardIndexPage),
});

/** A bare world lands on its default surface. */
const worldRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/worlds/$worldId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/worlds/$worldId/$surface', params: { worldId: params.worldId, surface: DEFAULT_WORLD_SURFACE } });
  },
});

/** One surface of one world; an unknown surface lands on the default. */
const worldSurfaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/worlds/$worldId/$surface',
  beforeLoad: ({ params }) => {
    if (!isWorldSurface(params.surface)) {
      throw redirect({ to: '/worlds/$worldId/$surface', params: { worldId: params.worldId, surface: DEFAULT_WORLD_SURFACE } });
    }
  },
  component: withSuspense(WorldSurfacePage),
});

/** Character Workspace — world-scoped, slug-based (canonical). */
const worldCharacterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/worlds/$worldId/characters/$characterSlug',
  component: withSuspense(CharacterWorkspacePage),
});

/** Location Workspace — world-scoped, slug-based (canonical). */
const worldLocationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/worlds/$worldId/locations/$locationSlug',
  component: withSuspense(EnvironmentWorkspacePage),
});

/** Legacy IP paths (before CDEV-2028) redirect to their worlds counterparts. */
const ipsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ips',
  beforeLoad: () => {
    throw redirect({ to: '/worlds' });
  },
});

const ipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ips/$ipId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/worlds/$worldId/$surface', params: { worldId: params.ipId, surface: DEFAULT_WORLD_SURFACE } });
  },
});

/**
 * Character Workspace route — legacy bare-id path. Same nondeterministic
 * behaviour as the legacy environment route (see the comment on
 * environmentRoute below): the backend's `character(id)` resolver
 * iterates every IP's domain store and returns the first match. With
 * `char-ensemble` existing in both Cavali and Big Fish, the breadcrumb
 * misattributes. Kept for backward compatibility with stale shared links;
 * useCharacter emits a console.warn on every bare-id lookup so we can
 * find remaining callers and migrate them.
 */
const characterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/characters/$characterId',
  component: withSuspense(CharacterWorkspacePage),
});

/** Legacy IP-scoped character path → its world (CDEV-2028). */
const ipScopedCharacterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ips/$ipId/characters/$characterSlug',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/worlds/$worldId/characters/$characterSlug',
      params: { worldId: params.ipId, characterSlug: params.characterSlug },
    });
  },
});

/**
 * Environment Workspace route — legacy bare-id path.
 *
 * This route loses IP context: when the same location slug exists in
 * multiple IPs (Cavali and Big Fish both have a "Big Top"), the backend's
 * `location(id)` resolver iterates every IP's domain store and returns
 * the first match, which is nondeterministic. Resulting breadcrumbs
 * misattribute the environment (e.g. /environments/loc-big_top loaded
 * from a Cavali context renders as "Big Fish / Big Top").
 *
 * Kept for backward compatibility with stale links; the `useLocation`
 * hook emits a console warning on every bare-id lookup. New navigation
 * uses the IP-scoped slug route below.
 */
const environmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/environments/$environmentId',
  component: withSuspense(EnvironmentWorkspacePage),
});

/** Legacy IP-scoped environment path → the world's location (CDEV-2028). */
const ipScopedEnvironmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ips/$ipId/environments/$environmentSlug',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/worlds/$worldId/locations/$locationSlug',
      params: { worldId: params.ipId, locationSlug: params.environmentSlug },
    });
  },
});

/**
 * Asset Library route
 */
const assetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets',
  component: withSuspense(AssetLibraryPage),
});

/**
 * Admin Edit User route
 */
const adminUserEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users/$userId',
  component: withSuspense(EditUserPage),
});

/**
 * Admin Users route
 */
const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: withSuspense(UsersPage),
});

/**
 * Admin People route: sign-ins and presence (CDEV-2247)
 */
const adminPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/people',
  component: withSuspense(PeoplePage),
});

/**
 * Admin Roles route
 */
const adminRolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles',
  component: withSuspense(RolesPage),
});

/**
 * Admin Grants route
 */
const adminGrantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/grants',
  component: withSuspense(GrantsPage),
});

/**
 * Admin Capabilities route
 */
const adminCapabilitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/capabilities',
  component: withSuspense(CapabilitiesPage),
});

/**
 * Admin Quorums route
 */
const adminQuorumsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/quorums',
  component: withSuspense(QuorumsPage),
});

/**
 * Admin Application Scopes route
 */
const adminScopesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/scopes',
  component: withSuspense(ApplicationScopesPage),
});

/**
 * Admin Keys route
 */
const adminKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/keys',
  component: withSuspense(KeysPage),
});

/**
 * Admin Providers route
 */
const adminProvidersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/providers',
  component: withSuspense(ProvidersPage),
});

/**
 * User Settings route
 */
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: withSuspense(SettingsPage),
});

/**
 * Observatory - system observability (mesh, access logs, diagnostics)
 */
const meshRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mesh',
  component: withSuspense(MeshPage),
});

/**
 * Nexus - Personal Domain Knowledge Graph
 *
 * Retired (CDEV-1995): app:nexus is DEPRECATED, so the shell drops it from the
 * rail, but the route stays reachable by URL until its parts have folded into
 * the Bridge.
 */
const nexusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nexus',
  component: withSuspense(NexusPage),
});

/**
 * Loom — Canonical Knowledge Surface (specs, diagrams, domain models)
 */
const loomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loom',
  component: withSuspense(LoomPage),
});

/**
 * Reading lists (CDEV-2199): the caller's curated lists of Loom editions, and
 * one list's page (members, curation, Share). Signed-in surfaces only.
 */
const readingListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loom/lists',
  component: withSuspense(ReadingListsPage),
});

const readingListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loom/lists/$listId',
  component: withSuspense(ReadingListPage),
});

/**
 * Spec Editor — authoring UI for Loom documents
 */
const specEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spec-editor',
  component: withSuspense(SpecEditorPage),
});

/**
 * Auth callback route - handles OIDC redirect
 */
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: withSuspense(AuthCallbackPage),
});

/**
 * Invite redemption route
 */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: withSuspense(InvitePage),
});

/**
 * Share landing (CDEV-2154): a shared edition is an invitation. Before sign-in
 * it names the document only when anyone may read it.
 */
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: withSuspense(SharePage),
});

/**
 * Work Management — redirect to teams
 */
const workRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work',
  component: withSuspense(WorkPage),
});

/**
 * Work — Teams listing
 */
const workTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams',
  component: withSuspense(WorkTeamsPage),
});

/**
 * Work — Team issues
 */
const workTeamIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams/$teamId/issues',
  component: withSuspense(WorkIssuesPage),
});

/**
 * Work — Single issue detail
 */
const workIssueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams/$teamId/issues/$issueId',
  component: withSuspense(WorkIssuePage),
});

/**
 * Work — Team sprints, the current one first, issues grouped by epic (CDEV-2440)
 */
const workTeamSprintsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams/$teamId/sprints',
  component: withSuspense(WorkSprintsPage),
});

/**
 * Work — Team projects
 */
const workTeamProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams/$teamId/projects',
  component: withSuspense(WorkProjectsPage),
});

/**
 * Work — Team labels
 */
const workTeamLabelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/teams/$teamId/labels',
  component: withSuspense(WorkLabelsPage),
});

/**
 * Work — All projects overview
 */
const workProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/projects',
  component: withSuspense(WorkProjectsOverviewPage),
});

/**
 * Work — Single project detail
 */
const workProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/projects/$projectId',
  component: withSuspense(WorkProjectPage),
});

/**
 * Catch-all 404 route
 */
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: withSuspense(NotFoundPage),
});

// Build the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  meshRoute,
  worldsIndexRoute,
  worldRoute,
  worldSurfaceRoute,
  worldCharacterRoute,
  worldLocationRoute,
  ipsIndexRoute,
  ipRoute,
  characterRoute,
  ipScopedCharacterRoute,
  environmentRoute,
  ipScopedEnvironmentRoute,
  assetsRoute,
  adminUserEditRoute,
  adminUsersRoute,
  adminPeopleRoute,
  adminRolesRoute,
  adminGrantsRoute,
  adminCapabilitiesRoute,
  adminQuorumsRoute,
  adminScopesRoute,
  adminKeysRoute,
  adminProvidersRoute,
  settingsRoute,
  nexusRoute,
  loomRoute,
  readingListsRoute,
  readingListRoute,
  specEditorRoute,
  workRoute,
  workTeamsRoute,
  workTeamIssuesRoute,
  workIssueRoute,
  workTeamSprintsRoute,
  workTeamProjectsRoute,
  workTeamLabelsRoute,
  workProjectsRoute,
  workProjectRoute,
  authCallbackRoute,
  inviteRoute,
  shareRoute,
  notFoundRoute,
]);

// Create and export the router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Type registration for TypeScript support
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
